/**
 * GroupTab — broker-agnostic group/copy trading.
 *
 * A group = leader + N member accounts (any mix of Alpaca/IBKR/Tradovate).
 * One EXECUTE fans the order out to every enabled member simultaneously
 * with per-member sizing (fixed / multiplier / pro-rata), applies the
 * group ATM template as brackets, and mints ONE Proof of Execution
 * certificate binding every member's broker_order_id — publicly
 * verifiable at omnistrat.ai/verify.
 *
 * Member credentials are encrypted server-side (AES-GCM) at rest and are
 * never returned by any endpoint.
 */
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '@/store';
import { api } from '@/lib/api';
import { ORG, GRN, MUT, FONT } from '@/theme';

const RED = '#e05252';

interface Member {
  member_id: string; label: string; broker: string;
  sizing_mode: string; size_value: number; creds_set: boolean; enabled: boolean;
  client_account?: boolean;
  // A third-party account cannot be traded until its holder consents.
  // Surfaced here so the leader sees it BEFORE firing rather than as a
  // per-member error afterwards.
  consented_at?: string | null;
  consent_required?: boolean;
  max_qty?: number | null;
  max_notional_usd?: number | null;
}
interface Group {
  group_id: string; name: string; leader_user_id: string;
  atm: { stop_pct: number; target_pct?: number; breakeven_pct?: number; trail_pct?: number } | null;
  members: Member[];
}
interface ExecResult {
  exec_id: string;
  results: Array<{
    member_id: string; label: string; broker: string; qty: number; ok: boolean;
    broker_order_id?: string; error?: string; latency_ms: number;
    // Entry accepted, protective stop NOT on the book.
    unprotected?: boolean; stop_error?: string;
  }>;
  cert_id: string | null; verify_url: string | null;
}

const inp: React.CSSProperties = {
  background: '#111', color: '#ddd', border: '1px solid #333', fontFamily: FONT,
  fontSize: 11, padding: '3px 6px', borderRadius: 2, width: 90,
};
const btn: React.CSSProperties = { ...inp, width: 'auto', cursor: 'pointer' };
const lbl: React.CSSProperties = { color: MUT, fontSize: 9, letterSpacing: 1 };

// Same scheme allowlist as NewsTab. This URL is server-supplied today,
// but an href built from API data should never be able to carry a
// javascript: scheme regardless of how trusted the source feels.
function safeHref(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || raw.length === 0) return undefined;
  try {
    const u = new URL(raw, window.location.origin);
    return (u.protocol === 'http:' || u.protocol === 'https:') ? u.href : undefined;
  } catch { return undefined; }
}

export function GroupTab() {
  const focus = useStore(s => s.focusSymbol);
  const [groups, setGroups] = useState<Group[]>([]);
  const [sel, setSel] = useState<string>('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  // Add-member form
  const [mLabel, setMLabel] = useState('');
  const [mBroker, setMBroker] = useState('alpaca');
  const [mSizing, setMSizing] = useState('multiplier');
  const [mValue, setMValue] = useState<number | ''>(1);
  const [mCreds, setMCreds] = useState<Record<string, string>>({});
  const [mClient, setMClient] = useState(false);

  // ATM form
  const [aStop, setAStop] = useState<number | ''>(1);
  const [aTgt, setATgt] = useState<number | ''>(2);
  const [aBe, setABe] = useState<number | ''>('');
  const [aTrail, setATrail] = useState<number | ''>('');

  // Execute form
  const [xSym, setXSym] = useState(focus);
  const [xSide, setXSide] = useState<'buy' | 'sell'>('buy');
  const [xQty, setXQty] = useState<number | ''>(100);
  const [xType, setXType] = useState<'market' | 'limit'>('market');
  const [xLim, setXLim] = useState<number | ''>('');
  const [lastExec, setLastExec] = useState<ExecResult | null>(null);

  useEffect(() => { setXSym(focus); }, [focus]);

  const load = useCallback(async () => {
    try {
      const d = await api<{ groups: Group[] }>('/api/terminal/groups');
      setGroups(d.groups);
      if (!sel && d.groups.length) setSel(d.groups[0].group_id);
    } catch (e) { toast.error((e as Error).message); }
  }, [sel]);
  useEffect(() => { void load(); }, [load]);

  const g = groups.find(x => x.group_id === sel) ?? null;

  const createGroup = async () => {
    if (!newName) return;
    try {
      const d = await api<{ group_id: string }>('/api/terminal/groups', { method: 'POST', body: JSON.stringify({ name: newName }) });
      setNewName(''); setSel(d.group_id); await load();
    } catch (e) { toast.error((e as Error).message); }
  };

  const credFields: Record<string, Array<{ k: string; label: string }>> = {
    alpaca:    [{ k: 'ALPACA_API_KEY', label: 'API key' }, { k: 'ALPACA_API_SECRET', label: 'API secret' }],
    ibkr:      [{ k: 'IBKR_ACCESS_TOKEN', label: 'Access token' }, { k: 'IBKR_ACCOUNT_ID', label: 'Account ID' }],
    tradovate: [{ k: 'TRADOVATE_ACCESS_TOKEN', label: 'Access token' }, { k: 'TRADOVATE_ACCOUNT_ID', label: 'Account ID' }],
  };

  const addMember = async () => {
    if (!g || !mLabel) { toast.error('Member label required'); return; }
    const creds = Object.fromEntries(Object.entries(mCreds).filter(([, v]) => v));
    try {
      await api(`/api/terminal/groups/${g.group_id}/members`, {
        method: 'POST',
        body: JSON.stringify({
          label: mLabel, broker: mBroker, sizing_mode: mSizing,
          size_value: typeof mValue === 'number' ? mValue : 1,
          creds: Object.keys(creds).length ? creds : undefined,
          client_account: mClient || undefined,
        }),
      });
      setMLabel(''); setMCreds({}); setMClient(false); await load();
      toast.success('Member added');
    } catch (e) { toast.error((e as Error).message); }
  };

  const toggleMember = async (m: Member) => {
    if (!g) return;
    await api(`/api/terminal/groups/${g.group_id}/members/${m.member_id}`, {
      method: 'POST', body: JSON.stringify({ enabled: !m.enabled }),
    }).catch(e => toast.error((e as Error).message));
    await load();
  };
  const removeMember = async (m: Member) => {
    if (!g) return;
    // Removing a funded member from a copy-trading group on a single click, with
    // no confirmation, is a destructive action on someone else's account.
    if (!window.confirm(`Remove ${m.label || m.member_id} from this group?\n\n` +
      `Their account will stop receiving group trades. Existing positions are NOT closed.`)) return;
    await api(`/api/terminal/groups/${g.group_id}/members/${m.member_id}`, { method: 'DELETE' })
      .catch(e => toast.error((e as Error).message));
    await load();
  };

  const saveAtm = async () => {
    if (!g) return;
    const atm = typeof aStop === 'number' && aStop > 0 ? {
      stop_pct: aStop,
      target_pct: typeof aTgt === 'number' ? aTgt : undefined,
      breakeven_pct: typeof aBe === 'number' ? aBe : undefined,
      trail_pct: typeof aTrail === 'number' ? aTrail : undefined,
    } : null;
    try {
      await api(`/api/terminal/groups/${g.group_id}/atm`, { method: 'POST', body: JSON.stringify({ atm }) });
      toast.success(atm ? 'ATM template saved' : 'ATM disabled'); await load();
    } catch (e) { toast.error((e as Error).message); }
  };

  const execute = async () => {
    if (!g) return;
    if (typeof xQty !== 'number' || xQty <= 0) { toast.error('Qty required'); return; }
    if (!Number.isFinite(xQty) || xQty > 1_000_000) { toast.error('Qty exceeds the per-order ceiling'); return; }

    // Copy-trade fan-out fires into every member account at once. This had
    // no confirmation at all, and the leader's own qty understates the
    // aggregate — each member is sized by their own multiplier/fixed rule,
    // so N members is N separate live orders in N separate accounts.
    const memberCount = g.members?.length ?? 0;
    if (memberCount === 0) { toast.error('This group has no members'); return; }
    const ok = window.confirm(
      `GROUP FIRE — ${g.name}\n\n` +
      `${xSide.toUpperCase()} ${xSym} ${xType.toUpperCase()}` +
      `${xType === 'limit' && typeof xLim === 'number' ? ` @ ${xLim}` : ''}\n` +
      `Leader qty: ${xQty}\n\n` +
      `This places SEPARATE live orders in ${memberCount} member account(s), ` +
      `each sized by that member's own rule — the total may be far larger ` +
      `than ${xQty}.\n\nFire this group?`
    );
    if (!ok) return;
    // A LIMIT order with no limit price silently became a MARKET order across
    // every member account: `typeof xLim === 'number' ? xLim : undefined` drops
    // the price and the venue fills at whatever is there. Refuse instead.
    if (xType === 'limit' && typeof xLim !== 'number') {
      toast.error('Limit price is required for a LMT group order — refusing to send (a missing limit would execute at market across every member account).');
      return;
    }

    setBusy(true); setLastExec(null);
    try {
      const d = await api<ExecResult>(`/api/terminal/groups/${g.group_id}/execute`, {
        method: 'POST',
        body: JSON.stringify({
          symbol: xSym, side: xSide, orderType: xType, qty: xQty,
          limitPrice: xType === 'limit' && typeof xLim === 'number' ? xLim : undefined,
          tif: 'day',
        }),
      });
      setLastExec(d);
      const accepted = d.results.filter(r => r.ok).length;
      // "filled" was wrong: a broker acknowledgement is not a fill, and on
      // this path a member can come back ok:true with stop_order_id null —
      // meaning the entry is live but its protective stop never landed.
      toast.success(`Group fire: ${accepted}/${d.results.length} accepted · attested — verify fills per account`);
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', gap: 24, fontFamily: FONT, fontSize: 11, height: '100%', overflow: 'auto' }}>
      {/* Groups + members */}
      <div style={{ minWidth: 330 }}>
        <div style={lbl}>TRADE GROUPS</div>
        <div style={{ display: 'flex', gap: 6, margin: '6px 0' }}>
          <select style={{ ...inp, width: 140 }} value={sel} onChange={e => setSel(e.target.value)}>
            {groups.map(x => <option key={x.group_id} value={x.group_id}>{x.name}</option>)}
          </select>
          <input style={inp} placeholder="New group…" value={newName} onChange={e => setNewName(e.target.value)} />
          <button style={btn} onClick={createGroup}>+ CREATE</button>
        </div>

        {g && (
          <>
            <div style={{ ...lbl, marginTop: 10 }}>MEMBERS · {g.members.length}</div>
            {g.members.map(m => (
              <div key={m.member_id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0', borderBottom: '1px solid #222' }}>
                <span style={{ color: m.enabled ? '#ddd' : MUT, minWidth: 110 }}>{m.label}</span>
                <span style={{ color: MUT, fontSize: 10, minWidth: 64 }}>{m.broker}</span>
                <span style={{ color: MUT, fontSize: 10, minWidth: 86 }}>{m.sizing_mode} ×{m.size_value}</span>
                <span style={{ fontSize: 9, color: m.creds_set ? GRN : MUT }}>{m.creds_set ? 'OWN CREDS' : 'PLATFORM'}</span>
                {m.client_account && <span style={{ fontSize: 9, color: ORG }}>CLIENT</span>}
                {/* Consent state and the member's own ceiling. Both were
                    invisible: a third-party member with no consent would
                    simply be skipped at fire time with no prior warning. */}
                {m.consent_required && (
                  <span style={{ fontSize: 9, color: RED, fontWeight: 700 }}
                        title="The account holder has not consented; this member will be skipped on execute.">
                    AWAITING CONSENT
                  </span>
                )}
                {m.client_account && m.consented_at && (
                  <span style={{ fontSize: 9, color: GRN }} title={`Consented ${m.consented_at}`}>CONSENTED</span>
                )}
                {(m.max_qty != null || m.max_notional_usd != null) && (
                  <span style={{ fontSize: 9, color: MUT }}>
                    CAP {m.max_qty != null ? `${m.max_qty}u` : ''}
                    {m.max_qty != null && m.max_notional_usd != null ? ' / ' : ''}
                    {m.max_notional_usd != null ? `$${m.max_notional_usd}` : ''}
                  </span>
                )}
                <button style={{ ...btn, fontSize: 9 }} onClick={() => toggleMember(m)}>{m.enabled ? 'ON' : 'OFF'}</button>
                <button style={{ ...btn, fontSize: 9, color: RED }} onClick={() => removeMember(m)}>✕</button>
              </div>
            ))}

            <div style={{ ...lbl, marginTop: 12 }}>ADD MEMBER</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 4 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={inp} placeholder="Label (Desk A)" value={mLabel} onChange={e => setMLabel(e.target.value)} />
                <select style={{ ...inp, width: 92 }} value={mBroker} onChange={e => { setMBroker(e.target.value); setMCreds({}); }}>
                  <option value="alpaca">Alpaca</option><option value="ibkr">IBKR</option><option value="tradovate">Tradovate</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <select style={{ ...inp, width: 92 }} value={mSizing} onChange={e => setMSizing(e.target.value)}>
                  <option value="fixed">fixed qty</option><option value="multiplier">multiplier</option><option value="pro_rata">pro-rata</option>
                </select>
                <input style={inp} type="number" step="0.1" value={mValue} onChange={e => setMValue(e.target.value === '' ? '' : +e.target.value)} />
              </div>
              {credFields[mBroker].map(f => (
                <input key={f.k} style={{ ...inp, width: 186 }} type="password" placeholder={`${f.label} (optional — delegated)`}
                       value={mCreds[f.k] ?? ''} onChange={e => setMCreds(c => ({ ...c, [f.k]: e.target.value }))} />
              ))}
              <label style={{ display: 'flex', gap: 6, alignItems: 'center', color: MUT, cursor: 'pointer' }}>
                <input type="checkbox" checked={mClient} onChange={e => setMClient(e.target.checked)} style={{ accentColor: ORG }} />
                Client account — third-party owner (Enterprise)
              </label>
              <button style={{ ...btn, width: 110 }} onClick={addMember}>ADD MEMBER</button>
              <div style={{ color: MUT, fontSize: 9, maxWidth: 300, lineHeight: 1.5 }}>
                Credentials are AES-GCM encrypted at rest, used only inside an execute call,
                never returned by any API. Use trade-only API keys (no withdrawal
                permissions) — funds never touch OmniStrat and stay at your broker.
                Empty = execute on platform credentials.
                Your own accounts, any broker mix: included in your tier. Anyone else&rsquo;s
                account or Passport (multi-seat, client copying): Enterprise, with a signed
                trading authorization (LOA) held by the leader.
              </div>
            </div>
          </>
        )}
      </div>

      {/* ATM + execute */}
      {g && (
        <div style={{ minWidth: 300 }}>
          <div style={lbl}>GROUP ATM (ADVANCED TRADE MGMT)</div>
          <div style={{ display: 'flex', gap: 6, margin: '6px 0', flexWrap: 'wrap' }}>
            <span style={{ color: RED }}>STOP%</span>
            <input style={{ ...inp, width: 52 }} type="number" step="0.1" value={aStop} onChange={e => setAStop(e.target.value === '' ? '' : +e.target.value)} />
            <span style={{ color: GRN }}>TGT%</span>
            <input style={{ ...inp, width: 52 }} type="number" step="0.1" value={aTgt} onChange={e => setATgt(e.target.value === '' ? '' : +e.target.value)} />
            <span style={{ color: MUT }}>BE@%</span>
            <input style={{ ...inp, width: 52 }} type="number" step="0.1" value={aBe} onChange={e => setABe(e.target.value === '' ? '' : +e.target.value)} />
            <span style={{ color: MUT }}>TRAIL%</span>
            <input style={{ ...inp, width: 52 }} type="number" step="0.1" value={aTrail} onChange={e => setATrail(e.target.value === '' ? '' : +e.target.value)} />
            <button style={btn} onClick={saveAtm}>SAVE</button>
          </div>
          <div style={{ color: MUT, fontSize: 9, maxWidth: 290, lineHeight: 1.5 }}>
            Applied to every member at entry. Alpaca: native OCO bracket.
            IBKR/Tradovate: platform-placed stop. BE@% moves stops to breakeven,
            TRAIL% then trails — managed server-side every minute.
            {g.atm ? ` Active: stop ${g.atm.stop_pct}%${g.atm.target_pct ? ` / tgt ${g.atm.target_pct}%` : ''}${g.atm.breakeven_pct ? ` / be ${g.atm.breakeven_pct}%` : ''}${g.atm.trail_pct ? ` / trail ${g.atm.trail_pct}%` : ''}.` : ' No template active.'}
          </div>

          <div style={{ ...lbl, marginTop: 14 }}>EXECUTE — SIMULTANEOUS, ALL MEMBERS</div>
          <div style={{ display: 'flex', gap: 6, margin: '6px 0', flexWrap: 'wrap' }}>
            <input style={{ ...inp, width: 70 }} value={xSym} onChange={e => setXSym(e.target.value.toUpperCase())} />
            <select style={{ ...inp, width: 62 }} value={xSide} onChange={e => setXSide(e.target.value as 'buy' | 'sell')}>
              <option value="buy">BUY</option><option value="sell">SELL</option>
            </select>
            <input style={{ ...inp, width: 62 }} type="number" value={xQty} onChange={e => setXQty(e.target.value === '' ? '' : +e.target.value)} />
            <select style={{ ...inp, width: 70 }} value={xType} onChange={e => setXType(e.target.value as 'market' | 'limit')}>
              <option value="market">MKT</option><option value="limit">LMT</option>
            </select>
            {xType === 'limit' && (
              <input style={{ ...inp, width: 70 }} type="number" step="0.01" placeholder="limit" value={xLim} onChange={e => setXLim(e.target.value === '' ? '' : +e.target.value)} />
            )}
            <button disabled={busy} style={{ ...btn, color: ORG, borderColor: ORG, fontWeight: 700 }} onClick={execute}>
              {busy ? 'FIRING…' : 'EXECUTE GROUP'}
            </button>
          </div>

          {lastExec && (
            <div style={{ marginTop: 8 }}>
              <div style={lbl}>RESULT · {lastExec.exec_id}</div>
              {lastExec.results.map(r => (
                <div key={r.member_id} style={{ display: 'flex', gap: 8, padding: '2px 0', borderBottom: '1px solid #222' }}>
                  {/* An accepted entry whose protective stop never landed is
                      NOT a clean fill — it is a naked position. Call it that. */}
                  <span style={{
                    color: !r.ok ? RED : (r.unprotected ? ORG : GRN),
                    minWidth: 34, fontWeight: r.unprotected ? 700 : 400,
                  }} title={r.unprotected ? `Stop NOT placed: ${r.stop_error ?? 'unknown'}` : undefined}>
                    {!r.ok ? 'REJ' : (r.unprotected ? 'NAKED' : 'ACK')}
                  </span>
                  <span style={{ minWidth: 100 }}>{r.label}</span>
                  <span style={{ color: MUT, minWidth: 60 }}>{r.broker}</span>
                  <span style={{ minWidth: 44 }}>×{r.qty}</span>
                  <span style={{ color: MUT, fontSize: 10 }}>{r.ok ? `${r.latency_ms}ms · ${r.broker_order_id ?? ''}` : r.error}</span>
                </div>
              ))}
              {lastExec.verify_url && (
                <a href={safeHref(lastExec.verify_url)} target="_blank" rel="noopener noreferrer" style={{ color: '#6bb6ab', fontSize: 10 }}>
                  PROOF OF EXECUTION — verify this group fire publicly →
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
